import { Vector } from '../shared/vector.ts';

import type { EntityId } from '../shared/entities.ts';
import { MAP_SIZE } from '../shared/parameters.ts';
import { SOCKET_PATH } from '../shared/protocol.ts';
import type { PlayerAction, Score } from '../shared/protocol.ts';
import type { WorldChanges } from '../shared/snapshot.ts';
import { WEAPONS } from '../shared/weapons.ts';
import {
  createWorld,
  getPlayer,
  moveBullets,
  reloadWeapon,
  startDash,
} from '../shared/world.ts';
import { createAudio } from './audio.ts';
import { connect } from './connection.ts';
import { listenToInput } from './input.ts';
import { createPrediction } from './prediction.ts';
import { createRenderer } from './renderer.ts';
import { createSmoother } from './smoothing.ts';

/** A tab in the background stops painting; waking it up must not teleport. */
const MAX_FRAME_MS = 200;

/** Where the camera and the ear sit while there is nobody to follow. */
const MAP_CENTER = Vector.of(MAP_SIZE / 2, MAP_SIZE / 2);

const canvas = document.querySelector<HTMLCanvasElement>('#world')!;
const hud = document.querySelector<HTMLElement>('#hud')!;
const scoreboard = document.querySelector<HTMLElement>('#scores')!;

const world = createWorld();
const renderer = createRenderer(canvas);
const smoother = createSmoother();
const audio = createAudio();
const prediction = createPrediction();
const connection = connect(serverUrl(), world, prediction, askName(), {
  onDeath,
  onScores: showScores,
  onChanges: playSounds,
});
const input = listenToInput(canvas, {
  onChange: values => connection.sendInput(values),
  onAction: act,
});

let isDead = false;
let lastFrame = performance.now();

canvas.addEventListener('click', () => {
  if (!isDead) return;

  isDead = false;
  connection.join();
});

requestAnimationFrame(step);

/**
 * Between two snapshots the client keeps playing: it moves its own player with
 * the input it just sent, and the bullets along the straight line they were
 * given. The next snapshot is the correction, not the news — and the frame is
 * written down so that correction can play it again from where the server says.
 */
function step(now: number) {
  const delta = Math.min(now - lastFrame, MAX_FRAME_MS);
  lastFrame = now;

  const playerId = connection.playerId();
  const player = playerId ? getPlayer(world, playerId) : undefined;

  if (player) {
    Object.assign(player.input, input.current());
    prediction.predict(player, delta);
  }

  moveBullets(world, delta);
  smoother.update(world, delta, playerId);
  renderer.render(world, smoother.positionOf, player?.position ?? MAP_CENTER);

  hud.textContent = statusLine(player);
  requestAnimationFrame(step);
}

function statusLine(player: ReturnType<typeof getPlayer>) {
  if (isDead) return 'You died. Click to respawn.';
  if (!player) return 'Connecting…';

  const weapon = WEAPONS[player.weapon];
  const ammo =
    player.reload > 0 ? 'reloading' : `${player.clip}/${weapon.clipSize}`;
  const sound = audio.isMuted() ? '   muted (m)' : '';

  return `${player.name}   ${ammo}   ${connection.latency()}ms${sound}`;
}

/**
 * Nothing on the wire ever says "he shot": a bullet that was not there a frame
 * ago is one, and the seven pellets of a shotgun are one between them, so the
 * shooters are collected before anything sounds.
 *
 * The arrival snapshot builds an arena that was already going. The only thing
 * in it that is happening now is us walking in, so that is the only thing heard.
 */
function playSounds(changes: WorldChanges, isArrival: boolean) {
  const playerId = connection.playerId();
  const me = playerId ? getPlayer(world, playerId) : undefined;
  const ear = me?.position ?? MAP_CENTER;
  const muzzles = new Map<EntityId, Vector>();

  for (const id of changes.created) {
    const entity = world.entities.get(id);

    if (entity?.kind === 'player') {
      if (!isArrival || id === playerId)
        audio.play('join', entity.position, ear);
    }

    if (entity?.kind === 'bullet' && !muzzles.has(entity.ownerId)) {
      muzzles.set(entity.ownerId, entity.position);
    }
  }

  if (isArrival) return;

  for (const [ownerId, muzzle] of muzzles) {
    const shooter = getPlayer(world, ownerId);
    if (shooter) audio.play(shooter.weapon, muzzle, ear);
  }

  for (const id of changes.reloaded) {
    const player = getPlayer(world, id);
    if (player) audio.play('reload', player.position, ear);
  }
}

/**
 * A one-off runs here first and travels afterwards, like the movement does. The
 * dash asks *this* copy whether it is allowed: the same rule on the same data,
 * so the two ends only part company if the cooldowns disagree — and when they
 * do, the hundred pixels it leaves are walked back by the replay rather than
 * left standing for the rest of the match.
 */
function act(action: PlayerAction) {
  const playerId = connection.playerId();
  const player = playerId ? getPlayer(world, playerId) : undefined;

  if (!player) return;

  if (action === 'dash') {
    if (player.dashCooldown > 0) return;
    startDash(player);
  } else {
    // Our own reload is predicted like our own movement, so the snapshot that
    // tells everyone else about it finds it already started here: this end is
    // the only one that can hear it begin.
    const wasReloading = player.reload > 0;

    reloadWeapon(player);

    if (!wasReloading && player.reload > 0) {
      audio.play('reload', player.position, player.position);
    }
  }

  connection.sendAction(action);
}

function onDeath() {
  isDead = true;
}

/**
 * Text in a corner that moves every few seconds: it is DOM, not canvas. On the
 * canvas it would be redrawn 120 times a second to say the very same thing.
 */
function showScores(scores: Score[]) {
  scoreboard.replaceChildren(
    ...scores.map(score => {
      const line = document.createElement('li');
      line.textContent = `${score.kills}  ${score.name}`;
      line.style.color = score.team;
      return line;
    }),
  );
}

/**
 * In production the page and the game server share a host, and nginx forwards
 * the socket path; `vite dev` serves the page itself, so the server is next
 * door on its own port.
 */
function serverUrl() {
  if (import.meta.env.DEV) return `ws://${location.hostname}:1337`;

  const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${location.host}${SOCKET_PATH}`;
}

function askName() {
  const stored = localStorage.getItem('nozombie.name');

  if (stored) return stored;

  const name = prompt('Your name')?.trim() || 'Anonymous';
  localStorage.setItem('nozombie.name', name);
  return name;
}
